import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Globe, Brain, FileCheck, Shield, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { BrandLogo, brandColors } from '../components/Brand';
import { motion } from 'framer-motion';

const Login = () => {
  const { t, i18n } = useTranslation();
  const [companyCode, setCompanyCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(companyCode, email, password);
    if (result.success) {
      toast.success('Login successful!');
      navigate('/dashboard');
    } else {
      toast.error(result.error || t('errors.invalidCredentials'));
    }
    setLoading(false);
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  const features = [
    { icon: FileCheck, title: 'Smart Document Workflows', desc: 'Automated approvals, version control & lifecycle tracking' },
    { icon: Brain, title: 'AI-Powered Intelligence', desc: 'Advanced Vision, OCR, risk scoring & 15+ AI engines' },
    { icon: Globe, title: 'Multilingual Support', desc: '15+ Indian & global languages with protected business terms' },
    { icon: Shield, title: 'Compliance & Risk Management', desc: 'Real-time risk scoring, fraud detection & audit trails' },
  ];

  const stats = [
    { value: '15+', label: 'AI Engines' },
    { value: '15', label: 'Languages' },
    { value: '99.9%', label: 'Uptime SLA' },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: 'var(--font-body)' }}>
      <div className="flex-1 flex">
        {/* Left Panel - Branding */}
        <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden" style={{ backgroundColor: '#0d1f36' }}>
          {/* Geometric pattern */}
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.04]" style={{ border: '1px solid white', transform: 'translate(30%, -30%)' }} />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-[0.04]" style={{ border: '1px solid white', transform: 'translate(-20%, 20%)' }} />
            <div className="absolute top-1/2 left-1/2 w-[200px] h-[200px] rounded-full opacity-[0.03]" style={{ border: '1px solid white', transform: 'translate(-50%, -50%)' }} />
            {/* Grid dots */}
            <div className="absolute inset-0 opacity-[0.03]" style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
              backgroundSize: '32px 32px'
            }} />
          </div>

          <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
            {/* Top - Logo & Brand */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-4 mb-12">
                <div className="bg-white rounded-2xl p-3 shadow-2xl">
                  <BrandLogo size="large" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                    ReasonDMS
                  </h1>
                  <p className="text-base mt-1 font-medium" style={{ color: '#e85d04' }}>
                    Secure Documents. Intelligent Decisions.
                  </p>
                </div>
              </div>

              <p className="text-xl text-slate-300 max-w-lg leading-relaxed font-light">
                India's most advanced AI-powered Document Intelligence Platform for enterprise organizations
              </p>
            </motion.div>

            {/* Middle - Features */}
            <div className="space-y-5 my-8">
              {features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + idx * 0.15 }}
                    className="flex items-start gap-4 group"
                  >
                    <div className="p-3 rounded-xl border border-white/10 bg-white/5 group-hover:bg-white/10 transition-colors duration-300 shrink-0">
                      <Icon className="w-5 h-5" style={{ color: '#2a9d8f' }} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white">{feature.title}</h3>
                      <p className="text-sm text-slate-400 mt-0.5">{feature.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Stats bar */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.8 }}>
              <div className="flex items-center gap-6 mb-8">
                {stats.map((stat, idx) => (
                  <React.Fragment key={idx}>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{stat.value}</p>
                      <p className="text-xs text-slate-400 mt-0.5 uppercase tracking-wider">{stat.label}</p>
                    </div>
                    {idx < stats.length - 1 && <div className="h-8 w-px bg-white/10" />}
                  </React.Fragment>
                ))}
              </div>

              {/* Made in India badges moved to footer */}
            </motion.div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="w-full lg:w-[45%] flex items-center justify-center p-6 lg:p-12 bg-slate-50">
          <motion.div
            className="w-full max-w-[400px]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Language Selector */}
            <div className="flex justify-end mb-8">
              <div className="flex items-center gap-2 bg-white rounded-lg px-2 py-1 border border-slate-200 shadow-sm">
                <Globe className="w-3.5 h-3.5 text-slate-500" />
                <Select value={i18n.language} onValueChange={changeLanguage}>
                  <SelectTrigger className="w-24 bg-transparent border-0 h-8 text-xs" data-testid="login-language-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="hi">Hindi</SelectItem>
                    <SelectItem value="bn">Bengali</SelectItem>
                    <SelectItem value="ta">Tamil</SelectItem>
                    <SelectItem value="te">Telugu</SelectItem>
                    <SelectItem value="mr">Marathi</SelectItem>
                    <SelectItem value="gu">Gujarati</SelectItem>
                    <SelectItem value="kn">Kannada</SelectItem>
                    <SelectItem value="ml">Malayalam</SelectItem>
                    <SelectItem value="pa">Punjabi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <div className="inline-flex items-center gap-3 mb-3">
                <div className="bg-white rounded-xl p-2 shadow-md border border-slate-100">
                  <BrandLogo size="default" />
                </div>
                <h1 className="text-2xl font-bold" style={{ color: brandColors.primary, fontFamily: 'var(--font-heading)' }}>ReasonDMS</h1>
              </div>
              <p className="text-sm text-slate-500">Secure Documents. Intelligent Decisions.</p>
            </div>

            {/* Login Card */}
            <Card className="shadow-lg border border-slate-200/80 bg-white" data-testid="login-card">
              <CardContent className="px-8 py-8">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold tracking-tight" style={{ color: brandColors.primary, fontFamily: 'var(--font-heading)' }}>
                    {t('auth.welcomeBack')}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1.5">{t('auth.signInToContinue')}</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className="text-sm font-medium mb-2 block text-slate-700">
                      {t('auth.companyCode')}
                    </label>
                    <Input
                      type="text"
                      data-testid="login-company-input"
                      placeholder="e.g., REASON"
                      value={companyCode}
                      onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
                      required
                      className="h-11 border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] bg-slate-50/50"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block text-slate-700">
                      {t('auth.email')}
                    </label>
                    <Input
                      type="email"
                      data-testid="login-email-input"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11 border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] bg-slate-50/50"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block text-slate-700">
                      {t('auth.password')}
                    </label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        data-testid="login-password-input"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="h-11 border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] pr-10 bg-slate-50/50"
                      />
                      <button
                        type="button"
                        data-testid="toggle-password-visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    data-testid="login-submit-button"
                    className="w-full h-12 text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                    style={{ backgroundColor: brandColors.accent }}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        {t('auth.loginButton')}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm text-slate-500">
                  {t('auth.noAccount')}{' '}
                  <Link
                    to="/register"
                    data-testid="register-link"
                    className="font-semibold hover:underline transition-colors"
                    style={{ color: brandColors.primary }}
                  >
                    {t('auth.register')}
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Bottom info */}
            <div className="mt-6 text-center">
              <p className="text-xs text-slate-400">
                Protected by enterprise-grade security
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full py-3 px-6 lg:px-12 flex items-center justify-between flex-wrap gap-4" style={{ backgroundColor: '#0a1929' }}>
        <div className="flex items-center gap-4 flex-wrap">
          {/* Logo + Version */}
          <div className="flex items-center gap-2.5">
            <div className="bg-white rounded-lg p-1.5">
              <BrandLogo size="small" />
            </div>
            <span className="text-sm font-semibold text-white" style={{ fontFamily: 'var(--font-heading)' }}>ReasonDMS</span>
            <span className="text-xs text-slate-400 font-medium">v2.0</span>
          </div>

          <div className="h-5 w-px bg-white/10 hidden sm:block" />

          {/* Made in India */}
          <div className="flex items-center gap-1">
            <span className="text-xs font-semibold" style={{ color: '#FF9933' }}>M</span>
            <span className="text-xs font-semibold text-white">a</span>
            <span className="text-xs font-semibold text-white">d</span>
            <span className="text-xs font-semibold text-white">e</span>
            <span className="text-xs text-white mx-0.5">in</span>
            <span className="text-xs font-semibold" style={{ color: '#138808' }}>India</span>
          </div>

          <div className="h-5 w-px bg-white/10 hidden sm:block" />

          {/* Badges */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-300 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 font-medium">DPIIT Recognized Startup</span>
            <span className="text-[10px] text-slate-300 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 font-medium">Startup India</span>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-right">
          <p className="text-[10px] text-slate-400">&copy; {new Date().getFullYear()} Reason Analytics Softech Pvt. Ltd.</p>
          <p className="text-[10px] text-slate-500">All Rights Reserved</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
